/**
 * Connection banner — the unmissable version of the connection state
 * (QA finding M2-B-02; PO ruling on M3.5). The §6.2 status pill
 * (`[data-testid="connection"]`) stays the machine mirror; this banner is
 * for the human mid-conversation who would never notice a <span> flip to
 * "closed" — six players on home wifi over three hours WILL drop, and
 * without this the verdict is "the VTT froze", not "my wifi hiccuped".
 *
 * Honest per state (the disabled-states-explain-themselves rule applied to
 * a connection): `reconnecting` says the client is trying; `closed` names
 * the remedy that actually exists today — reload. It never promises an
 * auto-reconnect the client doesn't do (that's M4 if it earns it).
 *
 * `suppressed` is for the cases where the banner would LIE: a removed seat
 * (the told-why notice owns that story — "reload to rejoin" is false when
 * the seat is gone) and the re-key flow's deliberate leave+rejoin.
 */

export interface ConnectionBannerOptions {
  /** A more specific surface owns the story; render nothing. */
  suppressed?: boolean;
}

/**
 * Render (or clear) the banner in `root` for `status`. Open/connecting
 * clears — the banner exists only when something is wrong. Idempotent.
 */
export function renderConnectionBanner(
  root: HTMLElement,
  status: string,
  options: ConnectionBannerOptions = {},
): void {
  root.replaceChildren();
  if (options.suppressed === true) return;
  if (status !== "reconnecting" && status !== "closed") return;

  const banner = document.createElement("div");
  banner.dataset["testid"] = "connection-banner";
  banner.dataset["status"] = status;

  if (status === "reconnecting") {
    banner.setAttribute("role", "status");
    banner.textContent = "Connection lost — reconnecting…";
  } else {
    banner.setAttribute("role", "alert");
    // Promise only what an offline client can guarantee: a reload always
    // reconnects, and the SERVER then tells the truth — same seat, or the
    // told-why notice if it was removed while offline (§5.2 layer 3 via the
    // E2E-13b front door). "Rejoin your seat" would promise a seat this
    // client cannot know still exists.
    banner.textContent = "Disconnected from the table. Reload the page to reconnect.";
  }
  root.appendChild(banner);
}
